<script setup>


import TicketService from "@/api/services/ticket.service";
import RunningClockTrackernet from "@/components/shared/RunningClockTrackernet.vue";
import TrackerService from "@/api/services/tracker.service";
import { loadRunningTimeEntries, loadTrackedTimes } from "@/views/internal-tickets/runningTimeEntries";
import Notification from "@/utils/plugins/NotificationService";
import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import moment from "moment/moment";
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useUiStore } from "@/app/stores/uiStore";
import stateColors from "@const/stateColors";
import priorityColors from "@const/priorityColors";
import { useTableSort } from "@/app/composables/useTableSort";
import { internalTicketDefaultSort, internalTicketSortFields } from "@/views/internal-tickets/internalTicketSortConfig";

const uiStore = useUiStore();
const router = useRouter();
// eslint-disable-next-line no-unused-vars
const emits = defineEmits(['totalTicket']);

const tickets = ref([]);
const totalResults = ref(0);
// const itemsPerPage = ref({id: 4, name: '5', value: 5});
const itemsPerPage = ref(5);
const page = ref(1);
let latestTicketsRequest = 0;
const sortFields = internalTicketSortFields;
const {
  applySortParams,
  getSortIcon,
  toggleSort,
} = useTableSort({ defaultSort: internalTicketDefaultSort });

const props = defineProps({
  departmentId: { type: [Number, String], required: true },
  refreshData: {
    type: Boolean,
    required: false
  },
  filters: {
    type: Object,
    required: false
  }
})

onMounted(async () => {
  await getTickets()
})

watch(() => props.refreshData, async () => {
  await getTickets()
})

watch(() => props.filters, async () => {
  page.value = 1
  await getTickets()
})
watch(() => props.departmentId, async () => {
  page.value = 1
  await getTickets()
})
/* const client = (ticket) => {
     return ticket.affectedUser ? ticket.affectedUser.name : (ticket.requestUser ? ticket.requestUser.name :'')
   }*/
const gotoEdit = (id) => {
  router.push({ name: 'update_internal_ticket', params: { id: id } })
}
const runningEntries = ref({});
const trackedTimes = ref({});

const getTickets = async () => {
  const requestId = ++latestTicketsRequest;
  const params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value,
    internal: true,
    ...props.filters,
    'department.id': props.departmentId,
  }
  applySortParams(params);
  uiStore.startLoading();
  try {
    const response = await TicketService.getTickets(params);

    if (requestId !== latestTicketsRequest) {
      return;
    }

    tickets.value = response.data['hydra:member'];
    [runningEntries.value, trackedTimes.value] = await Promise.all([
      loadRunningTimeEntries(tickets.value, TrackerService.getEntries),
      loadTrackedTimes(tickets.value, TrackerService.getTicketTotals),
    ]);
    totalResults.value = response.data["hydra:totalItems"];
    emits('totalTicket', totalResults.value);
  } catch (error) {
    console.log(error);
    Notification.addNotification('Error al obtener listado de tickets.', 'error', 6000);
  } finally {
    uiStore.stopLoading();
  }

}

const handlePageChange = async (newPage) => {
  page.value = newPage;
  await getTickets();
}

const handlePageSizeChange = async (newSize) => {
  itemsPerPage.value = newSize;
  page.value = 1;
  await getTickets();
}

const handleSortChange = async (field) => {
  toggleSort(field);
  page.value = 1;
  await getTickets();
}

const formatDateLocale = (value, loc = 'es') => {
  if (loc) {
    loc = 'es';
  }
  moment.updateLocale('es', {
    monthsShort: [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ]
  });
  if (value) {
    return moment(value).local(loc).format('DD/MM/YYYY HH:mm:ss');
  } else {
    return null;
  }
}

const typeColor = (type) => {
  if (type === 'Incidencia')
    return { color: '#F9156F', label: 'INCIDENCIA' };
  else if (type === 'Solicitud de servicio') {
    return { color: '#4AABC3', label: 'SERVICIO' };
  } else if (type === 'Solicitud de soporte (Duda)') {
    return { color: '#919191', label: 'SOPORTE' };
  }
  return 'grey'
}

</script>
<template>
  <div class="w-100">
    <el-row>
      <div v-if="tickets && tickets.length > 0" class="table pt-0 table-not-mobile">
        <table>
          <thead>
            <tr>
              <th>
                <div class="d-flex justify-content-start align-items-center pointer"
                  @click="handleSortChange(sortFields.code)">
                  <div>
                    <img width="21" height="20" class="mr-2" src="@/assets/icons/group.svg" alt="">CÓDIGO
                    <font-awesome-icon :icon="getSortIcon(sortFields.code)" />
                  </div>
                </div>
              </th>
              <th>
                <div class="d-flex justify-content-start align-items-center pointer"
                  @click="handleSortChange(sortFields.type)">
                  <div>
                    <img width="21" height="20" class="mr-2" src="@/assets/icons/group.svg" alt="">TIPO
                    <font-awesome-icon :icon="getSortIcon(sortFields.type)" />
                  </div>
                </div>
              </th>
              <th>
                <div class="d-flex justify-content-start align-items-center pointer"
                  @click="handleSortChange(sortFields.description)">
                  <div>
                    <img width="21" height="20" src="@/assets/icons/group.svg" alt=""> DESCRIPCIÓN
                    <font-awesome-icon :icon="getSortIcon(sortFields.description)" />
                  </div>
                </div>
              </th>
              <th style="width: 230px;">
                <div class="d-flex justify-content-start align-items-center pointer"
                  @click="handleSortChange(sortFields.createdAt)">
                  <div>
                    <img width="21" height="20" class="mr-2" src="@/assets/icons/calendar-blue.svg"
                      alt="fecha inicio">FECHA INICIO
                    <font-awesome-icon :icon="getSortIcon(sortFields.createdAt)" />
                  </div>
                </div>
              </th>
              <th style="width: 230px;">
                <div class="d-flex justify-content-start align-items-center pointer"
                  @click="handleSortChange(sortFields.daysOpen)">
                  <div>
                    <img width="21" height="20" class="mr-2" src="@/assets/icons/calendar-blue.svg"
                      alt="dias abierta">DIAS ABIERTA
                    <font-awesome-icon :icon="getSortIcon(sortFields.daysOpen)" />
                  </div>
                </div>
              </th>
              <!--            <th>-->
              <!--              <div-->
              <!--                  class="d-flex justify-content-start align-items-center pointer"-->
              <!--                  @click="changeOrderType()"-->
              <!--              >-->
              <!--                <div>-->
              <!--                  <img-->
              <!--                      width="21"-->
              <!--                      height="20"-->
              <!--                      class="mr-2"-->
              <!--                      src="@/assets/icons/group.svg"-->
              <!--                      alt="estado"-->
              <!--                  >PRIORIDAD-->
              <!--                  <font-awesome-icon-->
              <!--                      v-if="orderType === 'ASC'"-->
              <!--                      icon="sort-alpha-up"-->
              <!--                  />-->
              <!--                  <font-awesome-icon-->
              <!--                      v-else-if="orderType === 'DESC'"-->
              <!--                      icon="sort-alpha-down"-->
              <!--                  />-->
              <!--                  <font-awesome-icon-->
              <!--                      v-else-->
              <!--                      icon="sort"-->
              <!--                  />-->
              <!--                </div>-->
              <!--              </div>-->
              <!--            </th>-->
              <th>
                <div class="d-flex justify-content-start align-items-center pointer"
                  @click="handleSortChange(sortFields.status)">
                  <div>
                    <img width="21" height="20" class="mr-2" src="@/assets/icons/group.svg" alt="estado">ESTADO
                    <font-awesome-icon :icon="getSortIcon(sortFields.status)" />
                  </div>
                </div>
              </th>
              <th>
                <div class="d-flex justify-content-start align-items-center">
                  <img width="21" height="20" class="mr-2" src="@/assets/icons/tracker-net-blue.svg" alt="">TRACKERNET
                </div>
              </th>
              <th>
                <div class="d-flex justify-content-start align-items-center pointer"
                  @click="handleSortChange(sortFields.assignedTo)">
                  <div>
                    <img width="21" height="20" class="mr-2" src="@/assets/icons/user.svg" alt="">ASIGNADO A
                    <font-awesome-icon :icon="getSortIcon(sortFields.assignedTo)" />
                  </div>
                </div>
              </th>

            </tr>
          </thead>
          <tbody v-if="tickets && tickets.length && !uiStore.loading">
            <tr v-for="ticket in tickets" :key="ticket.id" class="ticket-row" tabindex="0"
              :aria-label="'Abrir ticket ' + ticket.tag + ' - ' + ticket.id" @click="gotoEdit(ticket.id)"
              @keydown.enter.self.prevent="gotoEdit(ticket.id)" @keydown.space.self.prevent="gotoEdit(ticket.id)">
              <td>
                <el-tooltip placement="top" effect="light">
                  <template #content>
                    <span :style="{ color: priorityColors.getColor(ticket.ticketPriority.name) }"> Prioridad {{
                      ticket.ticketPriority.name
                    }} </span>
                  </template>
                  <div class="d-flex justify-content-start align-items-center"
                    :style="{ color: priorityColors.getColor(ticket.ticketPriority.name) }">
                    [{{ ticket.tag }}] - {{ ticket.id }}
                    <font-awesome-icon class="ms-2 fa-2x" :icon="priorityColors.getIcon(ticket.ticketPriority.name)"
                      :style="{ color: priorityColors.getColor(ticket.ticketPriority.name) }" />
                  </div>
                </el-tooltip>
              </td>
              <td>
                <div class="d-flex justify-content-start align-items-center">
                  <div class="type-color" :style="{ backgroundColor: typeColor(ticket.ticketType?.name).color }" />
                  {{ typeColor(ticket.ticketType?.name).label }}
                </div>
              </td>
              <td>
                <div class="d-flex justify-content-start align-items-center">
                  {{ ticket.issue }}
                </div>
              </td>
              <td>
                <div class="d-flex justify-content-start align-items-center">
                  {{ formatDateLocale(ticket.createdAt) }}
                </div>
              </td>
              <td>
                <div class="d-flex justify-content-start align-items-center">
                  {{ ticket.daysOpen }}
                </div>
              </td>
              <!--            <td>-->
              <!--              <div-->
              <!--                  class="flex-row justify-content-between text-uppercase"-->
              <!--                  :style="{color: priorityColors.getColor(ticket.ticketPriority.name)}">-->
              <!--                {{ ticket.ticketPriority.name }}-->
              <!--              </div>-->
              <!--            </td>-->
              <td>
                <div class="flex-row justify-content-between" :style="{ color: stateColors.getColor(ticket.status) }">
                  {{ ticket.status }}
                </div>
              </td>
              <td>
                <RunningClockTrackernet v-if="runningEntries[ticket.id] || trackedTimes[ticket.id]" :entry="runningEntries[ticket.id]"
                  :total="trackedTimes[ticket.id]" />
              </td>
              <td>
                <div class="d-flex justify-content-between align-items-center">
                  <span v-if="ticket.assignedEmployee">{{ ticket.assignedEmployee.fullName }}</span>
                  <span v-else>SIN ASIGNAR</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="tickets && tickets.length > 0" class="table-mobile w-100">
        <el-card v-for="ticket in tickets" :key="ticket.id" class="ticket-mobile-card mb-3 pointer"
          role="link" tabindex="0" @click="gotoEdit(ticket.id)" @keydown.enter="gotoEdit(ticket.id)" @keydown.space.prevent="gotoEdit(ticket.id)">
          <div class="ticket-mobile-header">
            <div class="ticket-mobile-row ticket-mobile-row--inline ticket-mobile-code">
              <span><b class="ticket-mobile-label">Código:</b>
                <el-tooltip placement="top" effect="light">
                  <template #content>
                    <span :style="{ color: priorityColors.getColor(ticket.ticketPriority.name) }">
                      Prioridad {{ ticket.ticketPriority.name }}
                    </span>
                  </template>
                  <div class="d-flex align-items-center flex-nowrap gap-1"
                    :style="{ color: priorityColors.getColor(ticket.ticketPriority.name), whiteSpace: 'nowrap' }">
                    <span>[{{ ticket.tag }}] - {{ ticket.id }}</span>
                    <font-awesome-icon :icon="priorityColors.getIcon(ticket.ticketPriority.name)"
                      :style="{ color: priorityColors.getColor(ticket.ticketPriority.name) }" />
                  </div>
                </el-tooltip></span>
            </div>
          </div>
          <div class="ticket-mobile-row ticket-mobile-row--inline">
            <span class="ticket-mobile-label">Tipo:</span>
            <div class="d-flex align-items-center">
              <div class="type-color" :style="{ backgroundColor: typeColor(ticket.ticketType?.name).color }" />
              {{ typeColor(ticket.ticketType?.name).label }}
            </div>
          </div>
          <div class="ticket-mobile-row ticket-mobile-row--inline">
            <span class="ticket-mobile-label">Descripción:</span>
            <span>{{ ticket.issue }}</span>
          </div>
          <div class="ticket-mobile-row ticket-mobile-row--inline">
            <span class="ticket-mobile-label">Fecha inicio:</span>
            <span>{{ formatDateLocale(ticket.createdAt) }}</span>
          </div>
          <div class="ticket-mobile-row ticket-mobile-row--inline">
            <span class="ticket-mobile-label">Días abierta:</span>
            <span>{{ ticket.daysOpen }}</span>
          </div>
          <div class="ticket-mobile-row ticket-mobile-row--inline">
            <span class="ticket-mobile-label">Estado:</span>
            <span :style="{ color: stateColors.getColor(ticket.status) }">{{ ticket.status }}</span>
          </div>
          <div v-if="runningEntries[ticket.id] || trackedTimes[ticket.id]" class="ticket-mobile-row ticket-mobile-row--inline">
            <span class="ticket-mobile-label">Trackernet:</span>
            <RunningClockTrackernet :entry="runningEntries[ticket.id]" :total="trackedTimes[ticket.id]" />
          </div>
          <div class="ticket-mobile-row ticket-mobile-row--inline">
            <span class="ticket-mobile-label">Asignado a:</span>
            <span>{{ ticket.assignedEmployee ? ticket.assignedEmployee.fullName : 'SIN ASIGNAR' }}</span>
          </div>
        </el-card>
      </div>
      <div v-else>
        <p class="alert alert-info mt-3">
          No existen tickets para el departamento
        </p>
      </div>
    </el-row>
    <el-row>
      <SharedElementPagination class="pagination" v-model:current-page="page"
        :total="totalResults" v-model:page-size="itemsPerPage" :page-size-options="[5, 10, 20, 30, 50, 100, 200]"
        @current-change="handlePageChange"
        @size-change="handlePageSizeChange" />
    </el-row>
  </div>
</template>


<style scoped>
.ticket-row { cursor: pointer; }
.ticket-row:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; }
.type-color {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  margin-right: 5px;
}

.table-not-mobile {
  max-width: 100%;
  overflow-x: hidden;
}

.table-not-mobile table {
  table-layout: fixed;
  width: 100%;
}

.table-not-mobile table thead th {
  padding: 10px 6px;
  font-size: 15px;
  white-space: nowrap;
}

.table-not-mobile table thead th:nth-child(1) {
  width: 12% !important;
}

.table-not-mobile table thead th:nth-child(2) {
  width: 10% !important;
}

.table-not-mobile table thead th:nth-child(3) {
  width: 13% !important;
}

.table-not-mobile table thead th:nth-child(4) {
  width: 15% !important;
}

.table-not-mobile table thead th:nth-child(5) {
  width: 13% !important;
}

.table-not-mobile table thead th:nth-child(6) {
  width: 9% !important;
}

.table-not-mobile table thead th:nth-child(7) {
  width: 13% !important;
}

.table-not-mobile table thead th:nth-child(8) {
  width: 15% !important;
}

.table-not-mobile table thead th > div > div {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 3px;
  max-width: 100%;
  white-space: nowrap;
}

.table-not-mobile table thead th img,
.table-not-mobile table thead th svg {
  flex: 0 0 auto;
}

.table-not-mobile table tbody td {
  padding: 12px 8px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.table-not-mobile table tbody td > div {
  min-width: 0;
  max-width: 100%;
}

@media (max-width: 1199px) {
  .table-not-mobile table thead th {
    padding: 8px 4px;
    font-size: 13px;
  }

  .table-not-mobile table thead th img {
    width: 16px;
    height: 16px;
  }
}

@media (max-width: 991px) {
  .table-not-mobile table thead th {
    font-size: 12px;
  }

  .table-not-mobile table thead th img {
    width: 14px;
    height: 14px;
  }

  .table-not-mobile table tbody td {
    padding: 10px 6px;
  }
}

@media (min-width: 768px) {
  .table-mobile {
    display: none;
  }
}

@media (max-width: 767px) {
  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    margin-top: 15px;
  }

  .ticket-mobile-card {
    border-radius: 18px;
  }

  .ticket-mobile-card :deep(.el-card__body) {
    padding: 18px 18px 20px;
  }

  .ticket-mobile-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;
  }

  .ticket-mobile-row {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.85rem;
    word-break: break-word;
  }

  .ticket-mobile-row--inline {
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
  }

  .ticket-mobile-row--inline .ticket-mobile-label {
    min-width: 96px;
    flex-shrink: 0;
  }

  .ticket-mobile-row--inline> :last-child {
    min-width: 0;
    word-break: break-word;
  }

  .ticket-mobile-code {
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e8ebf6;
    margin-bottom: 0;
    flex: 1;
  }

  .ticket-mobile-label {
    font-weight: 700;
    color: #474ce6;
  }

  .ticket-mobile-actions {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    padding-top: 0;
    min-width: fit-content;
  }
}
</style>
